// What the room's page remembers between looks, kept in ONE place so that leaving can forget it all.
//
// Four small memories, all in the tab and none of them shared between people:
//   · reads     the raw answer of each room route, so a hop between sections answers at once
//   · offers    the day's offer, by room, as it was read (the words are held here, in memory only)
//   · stamps    the rooms whose "since you were last here" has already been marked seen
//   · kept      the tab's note that today's offer was looked at: a fact id and a day, never the words
//
// Every one is keyed by WHO IS LOOKING (the signed-in person's id, handed down by `RoomViewer`):
// a tab that signs out and in as someone else must never be answered from the last person's room.
// With no known viewer nothing is remembered at all: a component that cannot say whose page it is
// asks again each time. `forgetRoomCaches()` empties all four; sign-out calls it.
//
// Pure of React and of the network, so a test can read it directly.

import type { Offer } from "./shapes";

const SEP = "\u0000";

/** every sessionStorage key this page writes starts here, so one sweep can find them all */
export const ROOM_SESSION_PREFIX = "innernet:room-";

const keyOf = (viewer: string | null, ...parts: string[]): string | null => (viewer ? [viewer, ...parts].join(SEP) : null);

// ── reads ────────────────────────────────────────────────────────────────────

const reads = new Map<string, { at: number; json: unknown }>();

export const roomReads = {
  get(viewer: string | null, url: string): { at: number; json: unknown } | undefined {
    const k = keyOf(viewer, url);
    return k ? reads.get(k) : undefined;
  },
  set(viewer: string | null, url: string, json: unknown): void {
    const k = keyOf(viewer, url);
    if (k) reads.set(k, { at: Date.now(), json });
  },
  /** forget what was read at `url` (for everyone who read it), or everything */
  forget(url?: string): void {
    if (!url) return void reads.clear();
    for (const k of [...reads.keys()]) if (k.endsWith(SEP + url)) reads.delete(k);
  },
};

// ── the day's offer ──────────────────────────────────────────────────────────

/** null = looked, and nothing was offered today */
const offers = new Map<string, Offer | null>();

export const roomOffers = {
  has(viewer: string | null, roomId: string): boolean {
    const k = keyOf(viewer, roomId);
    return k ? offers.has(k) : false;
  },
  get(viewer: string | null, roomId: string): Offer | null {
    const k = keyOf(viewer, roomId);
    return (k ? offers.get(k) : null) ?? null;
  },
  set(viewer: string | null, roomId: string, offer: Offer | null): void {
    const k = keyOf(viewer, roomId);
    if (k) offers.set(k, offer);
  },
};

// ── "seen" stamps ────────────────────────────────────────────────────────────

const stamps = new Set<string>();

export const roomStamps = {
  has(viewer: string | null, roomId: string): boolean {
    const k = keyOf(viewer, roomId);
    return k ? stamps.has(k) : false;
  },
  add(viewer: string | null, roomId: string): void {
    const k = keyOf(viewer, roomId);
    if (k) stamps.add(k);
  },
  delete(viewer: string | null, roomId: string): void {
    const k = keyOf(viewer, roomId);
    if (k) stamps.delete(k);
  },
};

// ── the tab's note that today's offer was looked at ──────────────────────────
//
// sessionStorage keeps a fact id and a day. The sentence an offer was made in is made of
// someone's own words, and a tab's storage outlives a sign-out: it is never written there.
// The note says "looked at today", so a reload does not ask the room again for an offer it has
// already spent; it does not bring the sentence back.

export interface Kept {
  day: string;
  /** the fact that was offered; null = looked, and there was nothing to offer */
  factId: string | null;
}

const keptKey = (viewer: string, roomId: string) => `${ROOM_SESSION_PREFIX}offer:${viewer}:${roomId}`;

export const roomKept = {
  get(viewer: string | null, roomId: string, today: string): Kept | undefined {
    if (!viewer) return undefined;
    try {
      const raw = sessionStorage.getItem(keptKey(viewer, roomId));
      if (!raw) return undefined;
      const v = JSON.parse(raw) as { day?: unknown; factId?: unknown };
      if (v.day !== today) return undefined;
      return { day: today, factId: typeof v.factId === "string" && v.factId ? v.factId : null };
    } catch {
      return undefined;
    }
  },
  set(viewer: string | null, roomId: string, today: string, factId: string | null): void {
    if (!viewer) return;
    try {
      // exactly these two fields, whatever the caller holds
      sessionStorage.setItem(keptKey(viewer, roomId), JSON.stringify({ day: today, factId }));
    } catch {
      /* private mode: it lives for this page */
    }
  },
};

/**
 * Is there no point asking the room again today? Only when the tab knows the day's offer is
 * SPENT: answered (a yes, or a held one), or looked for and none made. A note that still names a
 * fact means the offer was shown and not answered, and the room serves that same fact again until
 * it is answered, so a reload asks again and "not now" (kept on the device, by fact id) hides it.
 */
export function offerIsSpent(kept: Kept | undefined): boolean {
  return kept !== undefined && kept.factId === null;
}

// ── forgetting ───────────────────────────────────────────────────────────────

/**
 * Forget everything the room's page remembers: what it read, the day's offer, which rooms it has
 * stamped, and the tab's own notes (the offer note, the baseline the netti pulse compares with).
 * Sign-out calls this, beside the chip's own forgetting.
 */
export function forgetRoomCaches(): void {
  reads.clear();
  offers.clear();
  stamps.clear();
  try {
    const gone: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(ROOM_SESSION_PREFIX)) gone.push(k);
    }
    for (const k of gone) sessionStorage.removeItem(k);
  } catch {
    /* no storage, or it would not answer: there is nothing there to forget */
  }
}
